import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, Search, ChevronDown } from "lucide-react";
import { WHATSAPP_URL } from "@/lib/whatsapp";
import { useI18n } from "@/lib/i18n";
import SearchDialog from "@/components/SearchDialog";
import { serviceNavItems } from "@/data/services";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  const home = lang === "en" ? "/en" : "/";
  const serviceItems = serviceNavItems(lang);

  const navLinks = [
    { label: t.nav.why[lang], href: "#why" },
    { label: t.nav.who[lang], href: "#who" },
    { label: t.nav.refs[lang], href: "#cases" },
  ];

  const goAnchor = (hash: string) => {
    navigate(`${home}${hash}`);
    const id = hash.replace("#", "");
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts++ < 40) setTimeout(tryScroll, 50);
    };
    setTimeout(tryScroll, 50);
  };

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/90 backdrop-blur-md"
    >
      <div className="container mx-auto flex items-center justify-between py-4 px-6 max-w-[1120px] gap-4">
        <Link to={home} className="flex items-baseline gap-0 shrink-0">
          <span className="font-display text-3xl text-brand-blue leading-none">A</span>
          <span className="font-display text-3xl text-brand-blue leading-none">-</span>
          <span className="font-display text-3xl text-foreground leading-none tracking-[0.15em]">WAVE</span>
        </Link>

        <div className="hidden lg:flex items-center gap-6 xl:gap-8 min-w-0">
          <button
            onClick={() => goAnchor("#why")}
            className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground hover:text-brand-blue transition-colors whitespace-nowrap"
          >
            {t.nav.why[lang]}
          </button>
          <button
            onClick={() => goAnchor("#who")}
            className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground hover:text-brand-blue transition-colors whitespace-nowrap"
          >
            {t.nav.who[lang]}
          </button>

          {/* Services dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              onClick={() => setServicesOpen((v) => !v)}
              className="flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground hover:text-brand-blue transition-colors whitespace-nowrap"
              aria-expanded={servicesOpen}
              aria-haspopup="true"
            >
              {t.nav.what[lang]}
              <ChevronDown size={12} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[340px]"
                >
                  <div className="bg-background border border-border rounded-sm shadow-lg overflow-hidden">
                    {serviceItems.map((item) => (
                      <Link
                        key={item.slug}
                        to={item.href}
                        onClick={() => setServicesOpen(false)}
                        className="block px-5 py-3 hover:bg-brand-off transition-colors border-b border-border last:border-b-0"
                      >
                        <div className="text-[13px] font-semibold text-foreground mb-0.5">
                          {item.label}
                        </div>
                        <div className="text-[11.5px] leading-snug text-muted-foreground">
                          {item.desc}
                        </div>
                      </Link>
                    ))}
                    <button
                      onClick={() => { setServicesOpen(false); goAnchor("#what"); }}
                      className="block w-full text-left px-5 py-3 bg-brand-off hover:bg-brand-off/70 transition-colors"
                    >
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-blue">
                        {lang === "en" ? "See the full system →" : "Bekijk het systeem →"}
                      </div>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={() => goAnchor("#cases")}
            className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground hover:text-brand-blue transition-colors whitespace-nowrap"
          >
            {t.nav.refs[lang]}
          </button>

          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground hover:text-brand-blue transition-colors"
            aria-label={lang === "nl" ? "Zoeken" : "Search"}
          >
            <Search size={14} />
            <span className="hidden xl:inline">⌘K</span>
          </button>
          <button
            onClick={() => setLang(lang === "nl" ? "en" : "nl")}
            className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground hover:text-brand-blue transition-colors"
            aria-label="Switch language"
          >
            <Globe size={14} />
            {lang === "nl" ? "EN" : "NL"}
          </button>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-primary text-primary-foreground px-5 py-2.5 rounded-sm text-xs font-semibold uppercase tracking-[0.2em] hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            {t.nav.cta[lang]}
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <button
            onClick={() => setSearchOpen(true)}
            className="text-foreground"
            aria-label={lang === "nl" ? "Zoeken" : "Search"}
          >
            <Search size={22} />
          </button>
          <button
            onClick={() => setLang(lang === "nl" ? "en" : "nl")}
            className="flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.2em] text-foreground"
            aria-label="Switch language"
          >
            <Globe size={16} />
            {lang === "nl" ? "EN" : "NL"}
          </button>
          <button
            onClick={() => setOpen(!open)}
            className="text-foreground"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>


      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="lg:hidden border-t border-border bg-background"
        >
          <div className="flex flex-col gap-4 p-6">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => { setOpen(false); goAnchor(link.href); }}
                className="text-left text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground hover:text-brand-blue transition-colors"
              >
                {link.label}
              </button>
            ))}

            {/* Mobile services accordion */}
            <div>
              <button
                onClick={() => setMobileServicesOpen((v) => !v)}
                className="w-full flex items-center justify-between text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground hover:text-brand-blue transition-colors"
              >
                {t.nav.what[lang]}
                <ChevronDown size={12} className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileServicesOpen && (
                <div className="mt-3 pl-3 border-l border-border space-y-3">
                  {serviceItems.map((item) => (
                    <Link
                      key={item.slug}
                      to={item.href}
                      onClick={() => setOpen(false)}
                      className="block"
                    >
                      <div className="text-[12.5px] font-semibold text-foreground">{item.label}</div>
                      <div className="text-[11px] text-muted-foreground leading-snug">{item.desc}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="bg-primary text-primary-foreground px-5 py-2.5 rounded-sm text-xs font-semibold uppercase tracking-[0.2em] text-center"
            >
              {t.nav.cta[lang]}
            </a>
          </div>
        </motion.div>
      )}
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </motion.nav>
  );
};

export default Navbar;
